<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
          </div>
        </section>
      </b-card>

      <b-row>
        <b-col lg="8" sm="12">
          <b-card header-tag="header">
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center">
                <span class="print-labels-card-title"><AppIcon name="barcode" class="mr-1" />Productos a etiquetar</span>
                <b-badge variant="primary" pill>
                  {{ totalLabels }} etiqueta{{ totalLabels == 1 ? '' : 's' }}
                </b-badge>
              </div>
            </template>

            <!-- Fila 1: origen (50%) + días/botón o buscador (50% restante) -->
            <div class="print-labels-toolbar">
              <b-form-group label="Origen de la lista" class="print-labels-toolbar__source">
                <b-form-select v-model="source" :options="arr.sources" size="sm" @change="onSourceChange" />
              </b-form-group>

              <div v-if="source !== 'manual'" class="print-labels-toolbar__query">
                <b-form-group label="Últimos (días)" class="print-labels-toolbar__days">
                  <b-form-input type="number" size="sm" min="1" v-model.number="query.days" @keyup.enter="fetchCandidates" />
                </b-form-group>
                <b-button variant="dark" size="sm" class="print-labels-toolbar__btn" @click="fetchCandidates">
                  <AppIcon name="search" class="mr-1" />Traer productos
                </b-button>
              </div>

              <div v-else class="print-labels-toolbar__finder">
                <FindObject :key="finderKey"
                            render="search"
                            type="products"
                            tag="Buscar producto"
                            :multiple="true"
                            @select-object="addItem($event)"
                            @select-objects="addItems($event)" />
              </div>
            </div>

            <!-- Fila 2: facetas del resultado (solo modos automáticos, cuando hay datos) -->
            <div v-if="source !== 'manual' && items.length" class="print-labels-facets">
              <b-form-group label="Proveedor" class="print-labels-facets__item">
                <v-select :options="supplierOptions" v-model="facets.supplier" placeholder="Todos" />
              </b-form-group>
              <b-form-group label="Categoría" class="print-labels-facets__item">
                <v-select :options="categoryOptions" v-model="facets.category" placeholder="Todas" @input="onCategoryChange" />
              </b-form-group>
              <b-form-group label="Subcategoría" class="print-labels-facets__item">
                <v-select :options="subcategoryOptions"
                          v-model="facets.subcategory"
                          :disabled="!facets.category"
                          :placeholder="facets.category ? 'Todas' : 'Elegí una categoría'" />
              </b-form-group>
            </div>

            <b-table class="app-table mt-2"
                     :items="filteredItems"
                     :fields="fields"
                     :stacked="isMobileViewport ? 'md' : false"
                     sticky-header="45vh"
                     small
                     v-if="filteredItems.length">
              <template v-slot:cell(detail)="data">
                <div class="print-labels-detail">
                  <span v-if="data.item.created_at" class="print-labels-detail__meta">
                    Alta {{ fDate(data.item.created_at) }}
                  </span>
                  <span v-if="form.show_price && form.price_list" class="print-labels-detail__price">
                    <AppIcon name="money"
                             class="print-labels-detail__price-icon"
                             v-b-tooltip.hover
                             :title="'Precio de venta ' + (form.show_price_iva ? 'con IVA' : 'sin IVA')" />
                    <b>{{ fPrice(form.show_price_iva ? data.item.price_amount_iva : data.item.price_amount, data.item.price_currency) }}</b>
                  </span>
                  <span v-if="data.item.cost_updated_at" class="print-labels-detail__meta">
                    Costo <b>{{ fPrice(data.item.cost_amount) }}</b>
                    <span v-if="data.item.cost_supplier">· {{ data.item.cost_supplier }}</span>
                    · {{ fDate(data.item.cost_updated_at) }}
                  </span>
                  <span v-if="!data.item.created_at && !data.item.cost_updated_at" class="text-muted">
                    Agregado manualmente
                  </span>
                </div>
              </template>
              <template v-slot:cell(copies)="data">
                <AppTextInput type="number"
                              size="sm"
                              min="1"
                              class="print-labels-copies"
                              v-model.number="data.item.copies" />
              </template>
              <template v-slot:cell(f_action)="data">
                <b-button variant="link"
                          class="app-table-icon-btn app-table-icon-btn--danger"
                          :class="{ 'app-table-icon-btn--labeled': isMobileViewport }"
                          v-b-tooltip.hover.left
                          title="Quitar producto"
                          @click="removeItem(data.item)">
                  <AppIcon name="delete" label="Quitar producto" />
                  <span v-if="isMobileViewport" class="ml-1">Quitar</span>
                </b-button>
              </template>
            </b-table>

            <EmptyState v-else-if="items.length"
                        icon="filter"
                        title="Ningún producto coincide con los filtros"
                        description="Ajustá proveedor, categoría o subcategoría, o limpiá los filtros." />

            <EmptyState v-else
                        :icon="source === 'manual' ? 'search' : 'barcode'"
                        :title="emptyTitle"
                        :description="emptyDescription" />

            <div v-if="items.length" class="app-table-footer print-labels-list-footer">
              <span class="list-view-toolbar__count">
                {{ filteredItems.length }} producto{{ filteredItems.length == 1 ? '' : 's' }}
                <span v-if="filteredItems.length !== items.length" class="text-muted">de {{ items.length }}</span>
              </span>
              <b-button variant="link" class="list-view-link" size="sm" @click="clearItems">
                <AppIcon name="delete" class="mr-1" />Vaciar lista
              </b-button>
            </div>
          </b-card>
        </b-col>

        <b-col lg="4" sm="12">
          <b-card header-tag="header">
            <template v-slot:header><span class="print-labels-card-title print-labels-card-title--options"><AppIcon name="tune" class="mr-1" />Opciones de impresión</span></template>

            <b-form-group label="Precio" class="print-labels-option-group mb-3">
              <b-form-checkbox v-model="form.show_price" switch size="sm" class="mb-2">
                Mostrar precios
              </b-form-checkbox>

              <template v-if="form.show_price">
                <b-form-checkbox v-model="form.show_price_iva" switch size="sm" class="mb-3">
                  Precios con IVA incluido
                </b-form-checkbox>
                <FindObject type="priceList"
                            tag="Lista de precios"
                            render="search"
                            :valueID="savedPriceListID"
                            :autoSelectSingle="true"
                            @select-object="setPriceList($event)" />
              </template>
            </b-form-group>

            <b-form-group label="Nombre a imprimir" class="print-labels-option-group mb-3">
              <b-form-radio-group v-model="form.name_field"
                                  :options="arr.nameFields"
                                  stacked
                                  size="sm" />
            </b-form-group>
            <b-form-group label="Código de barras" class="print-labels-option-group mb-3">
              <b-form-checkbox v-model="form.show_barcode" switch size="sm">
                Mostrar código de barras
              </b-form-checkbox>
            </b-form-group>

            <b-form-group label="Empezar en la celda"
                          class="print-labels-option-group mb-3"
                          description="Para reutilizar planchas de etiquetas ya usadas a la mitad.">
              <b-form-input type="number" size="sm" min="1" v-model.number="form.start_cell" />
            </b-form-group>

            <b-alert show variant="info" class="small mb-0">
              <AppIcon name="info" class="mr-1" />
              El diseño y el tamaño de la etiqueta se editan en
              <router-link :to="{ name: 'ReportsStaffDesign' }">Reportes → Diseño de reportes</router-link>
              (reporte «Etiquetas de Productos»).
            </b-alert>
          </b-card>
        </b-col>
      </b-row>

      <b-alert show
               variant="primary"
               v-if="filteredItems.length"
               class="d-flex align-items-center justify-content-between mb-3 py-2">
        <span class="print-labels-print-summary"><AppIcon name="print" class="mr-1" /><b>{{ totalLabels }}</b> etiqueta{{ totalLabels == 1 ? '' : 's' }} para imprimir</span>
        <b-button variant="success" size="sm" @click="printLabels()">
          <AppIcon name="printer" class="mr-1" />Imprimir etiquetas (PDF)
        </b-button>
      </b-alert>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" class="mr-1" />Volver
          </b-button>
        </div>
      </b-card>

      <Printer v-if="printer.filters"
               :reference="printer.reference"
               :filters="printer.filters"
               :key="printer.key" />
    </b-col>
  </b-row>
</template>

<script>
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Error from '@/handler/error'
  import StorageLocal from '@/handler/storageLocal'
  import AppTextInput from '@/components/inc/input/AppTextInput'
  import FindObject from '@/components/inc/find/findObject'
  import Printer from '@/components/inc/printer/printer'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import serviceAPI from './services'

  const STORAGE = {
    priceList: 'print_labels_price_list',
    showPrice: 'print_labels_show_price',
    showPriceIva: 'print_labels_show_price_iva',
    nameField: 'print_labels_name_field',
    showBarcode: 'print_labels_show_barcode',
  }

  export default {
    components: {
      AppTextInput,
      FindObject,
      Printer,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.PRODUCTOS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'printLabels',
          elements: {},
        },
        isMobileViewport: false,
        finderKey: 0,
        savedPriceListID: 0,
        source: 'new',
        items: [],
        query: {
          days: 7,
        },
        facets: {
          supplier: null,
          category: null,
          subcategory: null,
        },
        arr: {
          sources: [
            { value: 'new', text: 'Productos nuevos ingresados' },
            { value: 'price_change', text: 'Productos con cambio de precio' },
            { value: 'manual', text: 'Selección manual' },
          ],
          nameFields: [
            { value: 'short', text: 'Nombre corto' },
            { value: 'long', text: 'Nombre largo' },
          ],
        },
        form: {
          price_list: null,
          show_price: true,
          show_price_iva: false,
          name_field: 'short',
          show_barcode: true,
          start_cell: 1,
        },
        printer: {
          key: 0,
          reference: 'products-label',
          filters: null,
        },
      }
    },
    created() {
      Helper.hasAccessView(this.access)
      this.restoreOptions()
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      'form.show_price'() { this.persistOptions() },
      'form.show_price_iva'() { this.persistOptions() },
      'form.name_field'() { this.persistOptions() },
      'form.show_barcode'() { this.persistOptions() },
    },
    computed: {
      totalLabels() {
        return this.filteredItems.reduce((total, item) => total + Math.max(1, parseInt(item.copies) || 1), 0)
      },
      // Opciones de faceta: solo lo presente en el resultado traído. Subcategoría
      // se acota además a la categoría elegida.
      supplierOptions() {
        return this.distinctOptions(this.items, 'supplier_id', 'supplier')
      },
      categoryOptions() {
        return this.distinctOptions(this.items, 'category_id', 'category')
      },
      subcategoryOptions() {
        // Solo se carga si hay categoría elegida (es condición de la categoría).
        if(!this.facets.category) {
          return []
        }
        const rows = this.items.filter(item => item.category_id === this.facets.category.code)
        return this.distinctOptions(rows, 'subcategory_id', 'subcategory')
      },
      filteredItems() {
        return this.items.filter(item => {
          if(this.facets.supplier && item.supplier_id !== this.facets.supplier.code) return false
          if(this.facets.category && item.category_id !== this.facets.category.code) return false
          if(this.facets.subcategory && item.subcategory_id !== this.facets.subcategory.code) return false
          return true
        })
      },
      fields() {
        return [
          { key: 'code', label: 'Código', class: 'align-middle', thStyle: { width: '130px' } },
          { key: 'name', label: 'Producto', class: 'align-middle' },
          { key: 'detail', label: 'Detalle', class: 'align-middle' },
          { key: 'copies', label: 'Copias', class: 'align-middle ' + this.alignClass('text-center'), thStyle: { width: '110px' } },
          { key: 'f_action', label: this.isMobileViewport ? 'Quitar' : '', class: 'align-middle ' + this.alignClass('text-right'), thStyle: { width: '60px' } },
        ]
      },
      emptyTitle() {
        if(this.source === 'manual') return 'Todavía no agregaste productos'
        return 'Todavía no trajiste la lista'
      },
      emptyDescription() {
        if(this.source === 'manual') return 'Buscá un producto arriba para sumarlo a la plancha de etiquetas.'
        if(this.source === 'price_change') return 'Elegí un rango de días y traé los productos que cambiaron de precio.'
        return 'Elegí un rango de días y traé los productos dados de alta en ese período.'
      },
    },
    methods: {
      updateMobileViewport(event) {
        this.isMobileViewport = event.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      fDate(value) {
        return value ? this.moment(value).format('DD/MM/YYYY') : ''
      },
      fPrice(value, currency = 'ARS') {
        if(value === null || value === undefined) return 'Sin precio'
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: currency || 'ARS' }).format(value)
      },
      distinctOptions(rows, idKey, labelKey) {
        const seen = new Map()
        rows.forEach(row => {
          if(row[idKey] && !seen.has(row[idKey])) {
            seen.set(row[idKey], { code: row[idKey], label: row[labelKey] || '—' })
          }
        })
        return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label))
      },
      onCategoryChange() {
        // si la subcategoría elegida ya no pertenece a la categoría, se limpia
        if(this.facets.subcategory && !this.subcategoryOptions.some(o => o.code === this.facets.subcategory.code)) {
          this.facets.subcategory = null
        }
      },
      clearFacets() {
        this.facets.supplier = null
        this.facets.category = null
        this.facets.subcategory = null
      },
      restoreOptions() {
        const showPrice = StorageLocal.getValue(STORAGE.showPrice)
        const showPriceIva = StorageLocal.getValue(STORAGE.showPriceIva)
        const nameField = StorageLocal.getValue(STORAGE.nameField)
        const showBarcode = StorageLocal.getValue(STORAGE.showBarcode)
        const priceListID = StorageLocal.getValue(STORAGE.priceList)
        if(showPrice !== null) this.form.show_price = showPrice === 'true'
        if(showPriceIva !== null) this.form.show_price_iva = showPriceIva === 'true'
        if(nameField === 'short' || nameField === 'long') this.form.name_field = nameField
        if(showBarcode !== null) this.form.show_barcode = showBarcode === 'true'
        if(priceListID) this.savedPriceListID = parseInt(priceListID) || 0
      },
      persistOptions() {
        StorageLocal.setValue(STORAGE.showPrice, this.form.show_price ? 'true' : 'false')
        StorageLocal.setValue(STORAGE.showPriceIva, this.form.show_price_iva ? 'true' : 'false')
        StorageLocal.setValue(STORAGE.nameField, this.form.name_field)
        StorageLocal.setValue(STORAGE.showBarcode, this.form.show_barcode ? 'true' : 'false')
        StorageLocal.setValue(STORAGE.priceList, this.form.price_list ? String(this.form.price_list.id) : '')
      },
      setPriceList(priceList) {
        this.form.price_list = priceList
        this.savedPriceListID = priceList ? priceList.id : 0
        this.persistOptions()
        if(priceList && this.items.length) {
          this.loadItemPrices(this.items)
        }
      },
      onSourceChange() {
        this.finderKey++
      },
      fetchCandidates() {
        const days = Math.max(1, parseInt(this.query.days) || 0)
        if(!days) {
          this.$awn.alert('Indicá un rango de días válido.')
          return
        }

        let loader = this.$loading.show()
        serviceAPI.obtenerCandidatosEtiquetas({ mode: this.source, days: days, price_list_id: this.form.price_list ? this.form.price_list.id : 0 })
          .then((response) => {
            loader.hide()
            this.clearFacets()
            const rows = Array.isArray(response.data) ? response.data : []
            this.items = rows.map(row => ({
              products_id: row.products_id,
              code: row.code,
              name: row.name,
              copies: 1,
              created_at: this.source === 'new' ? row.created_at : null,
              cost_amount: this.source === 'price_change' ? row.cost_amount : null,
              cost_supplier: this.source === 'price_change' ? row.cost_supplier : null,
              cost_updated_at: this.source === 'price_change' ? row.cost_updated_at : null,
              price_amount: row.price_amount,
              price_amount_iva: row.price_amount_iva,
              price_currency: row.price_currency,
              supplier_id: row.supplier_id,
              category_id: row.category_id,
              subcategory_id: row.subcategory_id,
              supplier: row.supplier,
              category: row.category,
              subcategory: row.subcategory,
            }))

            if(!this.items.length) {
              this.$awn.warning('No se encontraron productos para ese período.')
            }
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
      addItem(object, keepFinder, deferPriceLoad = false) {
        if(!object) {
          return
        }

        const existing = this.items.find(item => item.products_id === object.id)
        if(existing) {
          existing.copies = (parseInt(existing.copies) || 1) + 1
        } else {
          const item = {
            products_id: object.id,
            code: object.code,
            name: object.name,
            copies: 1,
            created_at: null,
            cost_amount: null,
            cost_supplier: null,
            cost_updated_at: null,
            price_amount: null,
            price_amount_iva: null,
            price_currency: null,
          }
          this.items.push(item)
          if(this.form.price_list && !deferPriceLoad) {
            this.loadItemPrices([item])
          }
        }

        if(!keepFinder) {
          this.finderKey++
        }
      },
      addItems(objects) {
        if(!Array.isArray(objects) || !objects.length) {
          return
        }
        objects.forEach(object => this.addItem(object, true, true))
        if(this.form.price_list) {
          this.loadItemPrices(this.items)
        }
        this.finderKey++
      },
      loadItemPrices(items) {
        if(!this.form.price_list || !items.length) {
          return
        }

        serviceAPI.obtenerCandidatosEtiquetas({
          products_ids: items.map(item => item.products_id),
          price_list_id: this.form.price_list.id,
        })
        .then((response) => {
          const prices = new Map((response.data || []).map(item => [item.products_id, item]))
          items.forEach(item => {
            const price = prices.get(item.products_id)
            item.price_amount = price ? price.price_amount : null
            item.price_amount_iva = price ? price.price_amount_iva : null
            item.price_currency = price ? price.price_currency : null
          })
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      removeItem(item) {
        const index = this.items.indexOf(item)
        if(index !== -1) {
          this.items.splice(index, 1)
        }
      },
      clearItems() {
        this.items = []
        this.clearFacets()
      },
      printLabels() {
        if(!this.filteredItems.length) {
          return
        }

        this.printer.key = Math.random()
        this.printer.filters = {
          items: this.filteredItems.map(item => ({
            products_id: item.products_id,
            copies: Math.max(1, parseInt(item.copies) || 1),
          })),
          price_list_id: this.form.price_list ? this.form.price_list.id : 0,
          show_price: this.form.show_price,
          show_price_iva: this.form.show_price_iva,
          name_field: this.form.name_field,
          show_barcode: this.form.show_barcode,
          start_cell: Math.max(1, parseInt(this.form.start_cell) || 1),
        }
      },
    },
  }
</script>

<style scoped>
  .print-labels-copies {
    width: 80px;
  }
  .print-labels-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
  }
  .print-labels-toolbar__source {
    flex: 1 1 calc(50% - 8px);
    min-width: 260px;
    margin-bottom: 0;
  }
  .print-labels-toolbar__query {
    flex: 1 1 calc(50% - 8px);
    min-width: 240px;
    display: flex;
    align-items: flex-end;
    gap: 10px;
  }
  .print-labels-toolbar__days {
    width: 100px;
    flex-shrink: 0;
    margin-bottom: 0;
  }
  .print-labels-toolbar__btn {
    height: 31px;
    white-space: nowrap;
  }
  .print-labels-toolbar__finder {
    flex: 1 1 calc(50% - 8px);
    min-width: 240px;
  }
  /* El FindObject es un b-form-group con su margin-bottom propio: se anula para
     que la fila cierre igual que con el input de días (margin-bottom: 0). */
  .print-labels-toolbar__finder >>> .form-group {
    margin-bottom: 0;
  }
  .print-labels-facets {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 16px;
    padding: 10px 12px;
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
  }
  .print-labels-facets__item {
    flex: 1 1 200px;
    min-width: 160px;
    margin-bottom: 0;
  }
  .print-labels-detail {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
    line-height: 1.25;
  }
  .print-labels-detail__price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: var(--app-color-text);
  }
  .print-labels-detail__price-icon {
    color: var(--app-color-text-muted);
    font-size: 14px;
  }
  .print-labels-detail__meta {
    color: #718096;
    font-size: 11px;
  }
  .print-labels-detail__meta b {
    color: #4a5568;
    font-weight: 600;
  }
  .print-labels-card-title {
    color: inherit;
    font-weight: 600;
  }
  .print-labels-card-title--options {
    color: inherit;
  }
  .print-labels-option-group {
    padding: 10px 12px 2px;
    background: transparent;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
  }
  .print-labels-option-group >>> legend {
    width: auto;
    margin: -18px 0 6px -3px;
    padding: 0 5px;
    background: #fff;
    color: var(--app-color-text-secondary);
    font-size: 12px;
    font-weight: 600;
  }
  .print-labels-list-footer {
    margin-top: 4px;
  }
</style>
